import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { COMPANY_INFO } from '../data/pagesData'

export default function Header() {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false)
  const [cmsMenuItems, setCmsMenuItems] = useState(null)

  useEffect(() => {
    // Fetch menu items from CMS
    fetch('https://api.dev.auxo.dk/public/content/menu_items')
      ? fetch('https://api.dev.auxo.dk/public/content/menu_items')
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data) && data.length > 0) {
              setCmsMenuItems(data)
            }
          })
          .catch(err => console.error('Error fetching CMS menu items:', err))
      : null
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setServicesDropdownOpen(false)
  }, [location.pathname])

  // Prevent background scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  // Fallback services menu items if CMS not loaded yet
  const servicesList = [
    { label: "AC service", url: "/a-c-service/" },
    { label: "Dæk og fælge", url: "/daek-og-faelge/" },
    { label: "Gearkasse service", url: "/gearkasse-service/" },
    { label: "Gearkasse skylning", url: "/gearkasse-skylning/" },
    { label: "Kundebil", url: "/kundebil/" },
    { label: "Reparation", url: "/reparation/" },
    { label: "Service", url: "/service/" },
    { label: "Udskiftning af tandrem og taktkæde", url: "/udskiftning-af-tandrem-og-taktkaede/" },
    { label: "Walnut blaster", url: "/walnut-blaster/" }
  ]

  // If CMS items loaded, partition into top-level and children
  let menuServices = servicesList
  if (cmsMenuItems) {
    const cmsServices = cmsMenuItems
      .filter(item => item.parent_slug === 'vi-tilbyder')
      .sort((a, b) => (a.order || 0) - (b.order || 0))
    if (cmsServices.length > 0) {
      menuServices = cmsServices.map(s => ({ label: s.label, url: s.url }))
    }
  }

  const currentPath = location.pathname.endsWith('/') ? location.pathname : `${location.pathname}/`

  const isActive = (url) => {
    if (url === '/' && (location.pathname === '/' || location.pathname === '')) return true
    if (url !== '/' && currentPath === url) return true
    return false
  }

  const isServicesActive = menuServices.some(s => isActive(s.url))

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-xs">
      {/* Floating Sticky Right Actions (CTA buttons on right side) */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 flex flex-col gap-1 z-50">
        <a
          href={`tel:${COMPANY_INFO.phoneRaw}`}
          className="bg-[#046BB4] hover:bg-[#003E5E] text-white w-12 h-12 flex items-center justify-center shadow-lg transition-colors rounded-l-md cursor-pointer"
          title={`Ring ${COMPANY_INFO.phone}`}
        >
          <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
        </a>
        <a
          href={`mailto:${COMPANY_INFO.email}`}
          className="bg-[#046BB4] hover:bg-[#003E5E] text-white w-12 h-12 flex items-center justify-center shadow-lg transition-colors rounded-l-md cursor-pointer"
          title="Send mail til Sakskær Auto"
        >
          <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
        </a>
      </div>

      {/* Main Header & Navigation */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-0 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group py-1">
          <img
            src={COMPANY_INFO.logoUrl}
            alt="Sakskær Auto"
            className="h-16 sm:h-20 md:h-24 w-auto object-contain transition-transform group-hover:scale-[1.02]"
          />
        </Link>

        {/* Desktop Navigation (Visible ONLY at >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8" aria-label="Hovednavigation">
          <Link
            to="/"
            className={`text-base font-bold tracking-wide uppercase transition-colors ${
              isActive('/')
                ? 'text-[#046BB4] font-black'
                : 'text-slate-800 hover:text-[#046BB4]'
            }`}
          >
            Forside
          </Link>

          {/* Vi tilbyder Dropdown */}
          <div className="relative group">
            <button
              type="button"
              onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
              className={`text-base font-bold tracking-wide uppercase transition-colors flex items-center gap-1.5 cursor-pointer ${
                isServicesActive
                  ? 'text-[#046BB4] font-black'
                  : 'text-slate-800 hover:text-[#046BB4]'
              }`}
            >
              <span>Vi tilbyder</span>
              <i className="fa-solid fa-chevron-down text-xs transition-transform group-hover:rotate-180" aria-hidden="true" />
            </button>

            {/* Dropdown Menu */}
            <div className="absolute left-0 top-full pt-2 w-64 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 z-50">
              <div className="bg-white rounded-sm shadow-xl border border-slate-100 py-2 overflow-hidden">
                {menuServices.map((service, idx) => (
                  <Link
                    key={idx}
                    to={service.url}
                    className={`block px-4 py-2.5 text-sm font-medium uppercase transition-colors ${
                      isActive(service.url)
                        ? 'bg-blue-50 text-[#046BB4] font-bold border-l-4 border-[#046BB4]'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-[#046BB4]'
                    }`}
                  >
                    {service.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link
            to="/kontakt/"
            className="bg-[#046BB4] hover:bg-[#003E5E] text-white text-base font-bold px-6 py-2.5 rounded-sm transition shadow-xs uppercase whitespace-nowrap cursor-pointer"
          >
            Kontakt
          </Link>
        </nav>

        {/* Mobile Hamburger Button (Visible ONLY under 1200px) */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="min-[1200px]:hidden p-2.5 rounded-sm text-slate-800 hover:bg-slate-100 transition-colors border border-slate-200 cursor-pointer"
          aria-expanded={mobileMenuOpen}
          aria-label="Åbn/luk menu"
        >
          <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl text-slate-800`} aria-hidden="true" />
        </button>
      </div>

      {/* Mobile Sidebar Navigation Overlay/Drawer (Under 1200px) */}
      <div
        className={`fixed inset-0 z-[100] min-[1200px]:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!mobileMenuOpen}
      >
        {/* Dark Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 backdrop-blur-xs transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Sliding Sidebar Panel from Right */}
        <div
          className={`fixed top-0 right-0 bottom-0 w-full max-w-[360px] sm:max-w-[400px] bg-white z-[101] flex flex-col justify-between p-6 sm:p-8 shadow-2xl transition-transform duration-300 ease-in-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Header (Close Action) */}
          <div className="flex items-center justify-between pb-4 border-b border-slate-100">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Menu
            </span>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 -mr-2 text-black hover:opacity-70 transition-opacity cursor-pointer"
              aria-label="Luk menu"
            >
              <i className="fa-solid fa-xmark text-2xl text-[#0a0a0a]" aria-hidden="true" />
            </button>
          </div>

          {/* Main Navigation & Social Icons */}
          <div className="flex flex-col gap-6 flex-1 py-6 overflow-y-auto">
            <nav className="flex flex-col gap-5" aria-label="Mobilnavigation">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`text-xl font-bold uppercase tracking-wide transition-colors ${
                  isActive('/')
                    ? 'text-[#134257] font-black'
                    : 'text-[#0a0a0a] hover:text-[#134257]'
                }`}
              >
                Forside
              </Link>

              <div>
                <button
                  type="button"
                  onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                  className={`flex items-center justify-between w-full text-left text-xl font-bold uppercase tracking-wide cursor-pointer transition-colors ${
                    isServicesActive
                      ? 'text-[#134257] font-black'
                      : 'text-[#0a0a0a] hover:text-[#134257]'
                  }`}
                >
                  <span>Vi tilbyder</span>
                  <i
                    className={`fa-solid fa-chevron-down text-base transition-transform duration-200 ${
                      servicesDropdownOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {servicesDropdownOpen && (
                  <div className="flex flex-col gap-2.5 mt-3 pl-4 border-l-2 border-slate-200">
                    {menuServices.map((service, idx) => (
                      <Link
                        key={idx}
                        to={service.url}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`text-sm font-semibold uppercase tracking-wide transition-colors py-1 ${
                          isActive(service.url)
                            ? 'text-[#134257] font-bold'
                            : 'text-slate-600 hover:text-[#134257]'
                        }`}
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/kontakt/"
                onClick={() => setMobileMenuOpen(false)}
                className={`text-xl font-bold uppercase tracking-wide transition-colors ${
                  isActive('/kontakt/')
                    ? 'text-[#134257] font-black'
                    : 'text-[#0a0a0a] hover:text-[#134257]'
                }`}
              >
                Kontakt
              </Link>
            </nav>

            {/* Circular Social Media & Quick Contact Icons */}
            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
              {COMPANY_INFO.facebookUrl && (
                <a
                  href={COMPANY_INFO.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-11 h-11 rounded-full bg-[#f3f4f6] text-[#0a0a0a] hover:bg-[#134257] hover:text-white transition-colors"
                  title="Facebook"
                >
                  <i className="fa-brands fa-facebook-f text-base" aria-hidden="true" />
                </a>
              )}
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="flex items-center justify-center w-11 h-11 rounded-full bg-[#f3f4f6] text-[#0a0a0a] hover:bg-[#134257] hover:text-white transition-colors"
                title={`Ring ${COMPANY_INFO.phone}`}
              >
                <i className="fa-solid fa-phone text-base" aria-hidden="true" />
              </a>
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="flex items-center justify-center w-11 h-11 rounded-full bg-[#f3f4f6] text-[#0a0a0a] hover:bg-[#134257] hover:text-white transition-colors"
                title="Send e-mail"
              >
                <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
              </a>
              <a
                href={COMPANY_INFO.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-11 h-11 rounded-full bg-[#f3f4f6] text-[#0a0a0a] hover:bg-[#134257] hover:text-white transition-colors"
                title="Find os på Google Maps"
              >
                <i className="fa-solid fa-location-dot text-base" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Footer Logo */}
          <footer className="w-full pt-4 pb-2 flex flex-col items-center gap-1.5">
            <img
              src={COMPANY_INFO.logoUrl}
              alt="Sakskær Auto"
              className="h-12 w-auto object-contain"
            />
          </footer>
        </div>
      </div>
    </header>
  )
}
